import {
  Dropdown,
  DropdownItem,
  DropdownPosition,
  DropdownToggle,
  ToolbarItem,
} from '@patternfly/react-core'
import { FilterIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'

import { FilterCategories, FilterLabels } from './filters/filters'
import { LocationToolbarFilter } from './filters/location-toolbar-filter'
import { MRNToolbarFilter } from './filters/mrn-toolbar-filter'
import { PatientNameToolbarFilter } from './filters/patient-name-toolbar-filter'
import { StatusToolbarFilter } from './filters/status-toolbar-filter'
import { TypeToolbarFilter } from './filters/type-toolbar-filter'

export interface IFilters {
  location: string[]
  mrn: string[]
  patientName: string[]
  status: string[]
}

interface Props {
  allowedStatuses?: string[]

  location?: string
  onLocationUpdated?: (location: string) => void

  patientName?: string
  onPatientNameUpdated: (patientName: string) => void

  mrn?: string
  onMrnUpdated: (mrn: string) => void

  status?: string[]
  onStatusUpdated: (status: string[]) => void

  type?: string[]
  onTypeUpdated: (type: string[]) => void
}

export const VisitListFilter = ({
  allowedStatuses: allowedStatus,
  location,
  onLocationUpdated,
  patientName,
  onPatientNameUpdated,
  mrn,
  onMrnUpdated,
  status,
  onStatusUpdated,
  type,
  onTypeUpdated,
}: Props) => {
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState<boolean>(
    false,
  )
  const [currentCategory, setCurrentCategory] = useState<string>(
    FilterLabels[FilterCategories.Location],
  )

  const onCategorySelect = useCallback((event) => {
    setIsCategoryDropdownOpen(false)
    setCurrentCategory(event.target.innerText)
  }, [])

  const onCategoryToggle = useCallback((isOpen) => {
    setIsCategoryDropdownOpen(isOpen)
  }, [])

  const dropDownItems = []

  if (onLocationUpdated) {
    dropDownItems.push(
      <DropdownItem key={FilterCategories.Location}>
        {FilterLabels[FilterCategories.Location]}
      </DropdownItem>,
    )
  }

  if (onMrnUpdated) {
    dropDownItems.push(
      <DropdownItem key={FilterCategories.MRN}>
        {FilterLabels[FilterCategories.MRN]}
      </DropdownItem>,
    )
  }

  if (onPatientNameUpdated) {
    dropDownItems.push(
      <DropdownItem key={FilterCategories.PatientName}>
        {FilterLabels[FilterCategories.PatientName]}
      </DropdownItem>,
    )
  }

  if (onTypeUpdated) {
    dropDownItems.push(
      <DropdownItem key={FilterCategories.Type}>
        {FilterLabels[FilterCategories.Type]}
      </DropdownItem>,
    )
  }

  if (onStatusUpdated) {
    dropDownItems.push(
      <DropdownItem key={FilterCategories.Status}>
        {FilterLabels[FilterCategories.Status]}
      </DropdownItem>,
    )
  }

  return (
    <React.Fragment>
      <ToolbarItem>
        <Dropdown
          onSelect={onCategorySelect}
          position={DropdownPosition.left}
          toggle={
            <DropdownToggle onToggle={onCategoryToggle}>
              <FilterIcon /> {currentCategory}
            </DropdownToggle>
          }
          isOpen={isCategoryDropdownOpen}
          dropdownItems={dropDownItems}
        />
      </ToolbarItem>
      {onLocationUpdated ? (
        <LocationToolbarFilter
          location={location}
          onLocationUpdated={onLocationUpdated}
          showToolbarItem={
            currentCategory === FilterLabels[FilterCategories.Location]
          }
        />
      ) : null}

      {onPatientNameUpdated ? (
        <PatientNameToolbarFilter
          patientName={patientName}
          onPatientNameUpdated={onPatientNameUpdated}
          showToolbarItem={
            currentCategory === FilterLabels[FilterCategories.PatientName]
          }
        />
      ) : null}

      {onMrnUpdated ? (
        <MRNToolbarFilter
          mrn={mrn}
          onMrnUpdated={onMrnUpdated}
          showToolbarItem={
            currentCategory === FilterLabels[FilterCategories.MRN]
          }
        />
      ) : null}

      {onTypeUpdated ? (
        <TypeToolbarFilter
          type={type}
          onTypeUpdated={onTypeUpdated}
          showToolbarItem={
            currentCategory === FilterLabels[FilterCategories.Type]
          }
        />
      ) : null}

      {onStatusUpdated ? (
        <StatusToolbarFilter
          allowedStatuses={allowedStatus}
          status={status}
          onStatusUpdated={onStatusUpdated}
          showToolbarItem={
            currentCategory === FilterLabels[FilterCategories.Status]
          }
        />
      ) : null}
    </React.Fragment>
  )
}
